/**
 * React Hook Form용 Date / Month / DateRange 필드.
 */
import {
  DatePicker,
  DateRangePicker,
  MonthPicker,
} from '@/shared/components/DatePicker';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import * as React from 'react';
import type { Control, FieldPath, FieldValues, PathValue } from 'react-hook-form';

export interface FormDateFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>;
  name: TName;
  label: React.ReactNode;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const FormDateField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  required,
  placeholder,
  disabled,
  className,
}: FormDateFieldProps<TFieldValues, TName>) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem className={className}>
        <FormLabel required={required}>{label}</FormLabel>
        <FormControl>
          <DatePicker
            value={field.value ?? ''}
            onChange={field.onChange}
            placeholder={placeholder}
            disabled={disabled}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);

export interface FormMonthFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>;
  name: TName;
  label: React.ReactNode;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const FormMonthField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  required,
  placeholder,
  disabled,
  className,
}: FormMonthFieldProps<TFieldValues, TName>) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem className={className}>
        <FormLabel required={required}>{label}</FormLabel>
        <FormControl>
          <MonthPicker
            value={field.value ?? ''}
            onChange={field.onChange}
            placeholder={placeholder}
            disabled={disabled}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);

export interface FormDateRangeFieldProps<
  TFieldValues extends FieldValues,
  TFrom extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTo extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>;
  fromName: TFrom;
  toName: TTo;
  label: React.ReactNode;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/** from/to 두 필드를 하나의 RangePicker로 묶는다 */
export const FormDateRangeField = <
  TFieldValues extends FieldValues,
  TFrom extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTo extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  fromName,
  toName,
  label,
  required,
  placeholder,
  disabled,
  className,
}: FormDateRangeFieldProps<TFieldValues, TFrom, TTo>) => (
  <FormField
    control={control}
    name={fromName}
    render={({ field: fromField }) => (
      <FormField
        control={control}
        name={toName}
        render={({ field: toField }) => (
          <FormItem className={className}>
            <FormLabel required={required}>{label}</FormLabel>
            <FormControl>
              <DateRangePicker
                value={{ from: fromField.value, to: toField.value }}
                onChange={({ from, to }) => {
                  fromField.onChange(from as PathValue<TFieldValues, TFrom>);
                  toField.onChange(to as PathValue<TFieldValues, TTo>);
                }}
                placeholder={placeholder}
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    )}
  />
);
